import {Injectable} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {CartDialogComponent} from "./cart-dialog/cart-dialog.component";
import {RequestMapperService} from "../request-mapper.service";
import {Cart} from "../cart/interface/cart";
import {RaintreeResponse} from "../raintree/interface/raintree-response";
import {BehaviorSubject} from "rxjs";
import {CartTransmissionService} from "../raintree/transmission/cart/cart-transmission.service";
import {Product} from "../product-details/interface/product";
import {SizeProfileOptionItem} from "../product-details/interface/size-profile-option-item";
import {MadeToOrderProfile} from "../product-details/interface/made-to-order-profile";
import {CartItem} from "./interface/cart-item";
import {VolumeDiscountProfileItem} from "../product-details/interface/volume-discount-profile-item";

@Injectable({
  providedIn: 'root'
})
export class CartInformationService {

	private _items = new BehaviorSubject<CartItem[]>([]);
	public readonly items$ = this._items.asObservable();

	private _isOpen = new BehaviorSubject<boolean>(false);
	public readonly isOpen$ = this._isOpen.asObservable();

	constructor(
		private _dialog: MatDialog,
		private _cartApi: CartTransmissionService
	) {
		//TODO: Save to storage on every change
		// this.items$.subscribe(items => localStorage.setItem('cart', JSON.stringify(items)));
	}

	private _getCartData(openCart: boolean = false) {
		this._cartApi.getCartItemV2(
			() => {
			},
			() => {
			},
			(response: CartItem[]) => {
				this._items.next(response);
				if (response.length > 0) {
					this._isOpen.next(openCart);
					if(openCart) {
						this.openCartDialog();
					}
				}
				else {
					this._isOpen.next(false);
					this._dialog.closeAll();
				}
			},
			(): void => {
			},
			(): void => {
			}
		)
	}

	private _updateCartData(data: CartItem) {
		if (!data.selectedSizeOption) delete data.selectedSizeOption;
		if (data.productGroup === 'swatch') data.orderType = 'IN_STOCK';

		if(data.fabricProductPreview)
			data.fabricProductId = data.fabricProductPreview.id;
		else if(data.finishedProductPreview)
			data.finishedProductId = data.finishedProductPreview.id;


		this._cartApi.updateCartItemV2(
			data,
			() => {
			},
			() => {
			},
			(response: RaintreeResponse) => {
				// this.fetchCartFromAPI();
				this._items.next(this.items);
			},
			(error: string) => {
			},
			() => {
			}
		)
	}

	private _deleteCartData(id: number) {
		this._cartApi.deleteCartItem(
			RequestMapperService.DELETE_CART_ITEM + id,
			{} as Cart,
			() => {
			},
			() => {
			},
			(response: RaintreeResponse) => {
				this.fetchCartFromAPI();
			},
			(error: string) => {
			},
			() => {
			}
		)
	}

	private loadFromStorage(): CartItem[] {
		const stored = localStorage.getItem('cart');
		return stored ? JSON.parse(stored) : [];
	}

	public get items(): CartItem[] {
		return this._items.value;
	}

	public get isOpen(): boolean {
		return this._isOpen.value;
	}

	public openCart() {
		this._isOpen.next(true);
		this.openCartDialog();
	}

	public closeCart() {
		this._isOpen.next(false);
	}

	public toggleCart(open: boolean) {
		this._isOpen.next(open);
	}

	public clearCart(): void {
		this._items.next([]);
	}

	public fetchCartFromAPI(openCart: boolean = false) {
		this._getCartData(openCart);
	}

	public addItem(): void {
		this.fetchCartFromAPI();
	}

	public updateQty(cartItem: CartItem) {
		this._updateCartData(cartItem);
	}

	public removeItem(id: number) {
		this._deleteCartData(id);
	}

	public openCartDialog() {
		this._dialog.open(CartDialogComponent, {
			id: 'cart-dialog',
			maxWidth: '680px',
			width: '98%',
			height: '100%',
			position: {
				top: '0',
				right: '0'
			},
		});
	}

	public calculateFabricProductQuantity(
		product: Product,
		selectedFabric: Product | undefined,
	): number {
		let calculatedQuantityInStock: number = 0;
		if ((!product.totalQuantity || selectedFabric) && product.madeToOrderProfileEnabled && product.madeToOrderProfile) {
			const consumedFabric: number =  this.calculatedMTOConsumedFabric(product.madeToOrderProfile);
			const quantity: number = selectedFabric ? selectedFabric.totalQuantity : product.madeToOrderFabric ? product.madeToOrderFabric.totalQuantity : product.totalQuantity;
			calculatedQuantityInStock = quantity > 0 ? Math.floor(quantity / consumedFabric): 0;
		}
		else {
			calculatedQuantityInStock = product.totalQuantity;
		}
		return calculatedQuantityInStock;
	}

	public calculateFinishProductQuantity(
		product: Product,
		selectedFabric: Product | undefined,
		selectedSize: SizeProfileOptionItem | undefined,
		isCustomSizeSelected: boolean,
	): number {
		let calculatedQuantityInStock: number = 0;
		let consumedFabricSize: number = -1;
		let isDefaultFabric: boolean = selectedFabric ? selectedFabric.sku === product.madeToOrderFabric?.sku: true;

		if (selectedSize || isCustomSizeSelected) {
			if(selectedSize) {
				consumedFabricSize = selectedSize.consumedFabric;
				if(product.productSizeProfileList && product.productSizeProfileList.length > 0) {
					const productSize = product.productSizeProfileList.find(size => size.sizeProfileOption.label === selectedSize.label);
					if(productSize && productSize.consumedFabric) {
						consumedFabricSize = productSize.consumedFabric;
					}
					const sizeInStock = product.productSizeProfileList.filter(size => !size.disabled).find(size => size.sizeProfileOption.label === selectedSize.label);
					if(sizeInStock && isDefaultFabric) {
						calculatedQuantityInStock = sizeInStock.quantity;
					}
				}
			}
			if (!calculatedQuantityInStock && product.madeToOrderProfileEnabled && product.madeToOrderProfile) {
				const consumedFabric: number =  (consumedFabricSize > 0)? consumedFabricSize : this.calculatedMTOConsumedFabric(product.madeToOrderProfile);
				const quantity: number = selectedFabric ? selectedFabric.totalQuantity : product.madeToOrderFabric ? product.madeToOrderFabric.totalQuantity : product.totalQuantity;
				calculatedQuantityInStock = quantity > 0 ? Math.floor(quantity / consumedFabric): 0;
			}
			else {
				calculatedQuantityInStock = calculatedQuantityInStock > 0 || product.sizeProfileEnabled ? calculatedQuantityInStock : product.totalQuantity;
			}
		}
		else {
			calculatedQuantityInStock = selectedFabric && selectedFabric.sku !== product.madeToOrderFabric?.sku ? calculatedQuantityInStock: product.totalQuantity;
			if (product.madeToOrderProfileEnabled && product.madeToOrderProfile) {
				const consumedFabric: number =  (consumedFabricSize > 0)? consumedFabricSize : this.calculatedMTOConsumedFabric(product.madeToOrderProfile);
				let quantity: number = 0;
				if(!calculatedQuantityInStock) {
					quantity = selectedFabric ? selectedFabric.totalQuantity : product.madeToOrderFabric ? product.madeToOrderFabric.totalQuantity : product.totalQuantity;
					calculatedQuantityInStock = quantity > 0 ? Math.floor(quantity / consumedFabric): 0;
				}
			}
		}
		return calculatedQuantityInStock;
	}


	public calculateFinishProductPrice(
		product: Product,
		selectedFabricPrice: number,
		totalFinishPrice: number,
		customSizePrice: number,
		consumedFabric: number
	): number {
		let calculatedPrice: number = 0;
		let fabricPrice: number = selectedFabricPrice * consumedFabric;
		calculatedPrice = product.price + fabricPrice + totalFinishPrice + customSizePrice;
		// console.log(product.price, fabricPrice, totalFinishPrice, customSizePrice);
		return calculatedPrice;
	}

	public calculateFabricProductPrice(
		product: Product,
		selectedFabricPrice: number,
		totalFinishPrice: number,
		customSizePrice: number,
		consumedFabric: number
	): number {
		let calculatedPrice: number = 0;
		let selectedMadeToOrderFabricPrice: number = selectedFabricPrice;
		let madeToOrderFabricPrice: number = product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
		let fabricPrice: number = selectedMadeToOrderFabricPrice * consumedFabric;
		let makingCharges: number = product.price - madeToOrderFabricPrice;
		calculatedPrice = makingCharges + fabricPrice + totalFinishPrice + customSizePrice;
		// calculatedPrice = makingCharges + totalFinishPrice + customSizePrice;
		// if(product.madeToOrderFabric) //in case there is no made to order fabric anymore
		// 	calculatedPrice += fabricPrice;
		// console.log(makingCharges, fabricPrice, totalFinishPrice, customSizePrice);
		return calculatedPrice;
	}

	public getConsumedFabric(product: Product, selectedSizeOption: SizeProfileOptionItem | undefined): number {
		let consumedFabric: number = 1;
		if(product.productGroup !== 'finished') {
			consumedFabric = product.madeToOrderProfileEnabled &&product.madeToOrderProfile ? this.calculatedMTOConsumedFabric(product.madeToOrderProfile) : 1;
		}
		else {
			if(selectedSizeOption) {
				consumedFabric = selectedSizeOption.consumedFabric;
				if(product.productSizeProfileList && product.productSizeProfileList.length > 0) {
					let productSize = product.productSizeProfileList
						.find(size => size.sizeProfileOption.label === selectedSizeOption.label);
					if (productSize && productSize.consumedFabric) {
						consumedFabric = productSize.consumedFabric;
					}
				}
			}
			else if(product.madeToOrderProfileEnabled && product.madeToOrderProfile) {
				consumedFabric = product.madeToOrderProfile.consumedFabric;
			}
		}
		return  consumedFabric;
	}

	public calculateCartItemPrice(
		cartItem: CartItem,
		selectedFabricPrice: number,
		selectedFinishPrice: number,
		customSizePrice: number,
		product: Product
	): void {
		if(product.productGroup !== 'finished') {
			let consumedFabric: number = this.getConsumedFabric(product, undefined);
			cartItem.calculatedPrice = this.calculateFabricProductPrice(
				product,
				selectedFabricPrice,
				selectedFinishPrice,
				customSizePrice,
				consumedFabric
			);
			this.calculateCartItemVDPrice(product, selectedFinishPrice, customSizePrice, cartItem, consumedFabric);
		}
		else {
			let consumedFabric: number = this.getConsumedFabric(product, cartItem.selectedSizeOption);
			cartItem.calculatedPrice = this.calculateFinishProductPrice(
				product,
				selectedFabricPrice,
				selectedFinishPrice,
				customSizePrice,
				consumedFabric
			);
			this.calculateCartItemVDPrice(product, selectedFinishPrice, customSizePrice, cartItem, consumedFabric);
		}
	}

	public calculateCartItemVDPrice(
		product: Product,
		selectFinishPrice: number,
		selectCustomSizePrice: number,
		cartItem: CartItem,
		consumedFabric: number
	): void {
		cartItem.calculatedDiscountPrice = 0;
		if (product.volumeDiscountProfileEnabled && product.volumeDiscountProfile) {
			const vDList: VolumeDiscountProfileItem[] = product.volumeDiscountProfile.volumeDiscountProfileItemList.sort((a, b) => b.minimumOrderQuantity - a.minimumOrderQuantity);
			for (const item of vDList) {
				if (item.minimumOrderQuantity <= cartItem.quantity) {
					cartItem.calculatedDiscountPrice = this.calculateVDProductPrice(
						product,
						cartItem.selectedFabric?.product,
						selectFinishPrice,
						selectCustomSizePrice,
						item,
						item.minimumOrderQuantity,
						consumedFabric
					);
					break;
				}
			}
		}
	}

	public calculateVDProductPrice(
		product: Product,
		selectedFabric: Product | undefined,
		selectFinishPrice: number,
		selectCustomSizePrice: number,
		selectedVDProfile: VolumeDiscountProfileItem,
		quantity: number,
		consumedFabric: number
	): number {
		let calculatedPrice: number = 0;
		let selectedMadeToOrderFabricPrice: number = 0;
		let fabricPrice: number = 0;
		let makingCharges: number = 0;
		let selectedFabricQuantity: number = quantity;
		let volumeDiscountProfileItemList: VolumeDiscountProfileItem[] = [];
		if(product.productGroup === 'finished') {
			selectedMadeToOrderFabricPrice = selectedFabric ? selectedFabric.price : product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
			fabricPrice = selectedMadeToOrderFabricPrice * consumedFabric;
			selectedFabricQuantity = quantity * consumedFabric;
		}
		else {
			selectedMadeToOrderFabricPrice = selectedFabric ? selectedFabric.price : product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
			let madeToOrderFabricPrice: number = product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
			fabricPrice = selectedMadeToOrderFabricPrice * consumedFabric;
			makingCharges = product.price - madeToOrderFabricPrice;
		}

		if(selectedFabric && selectedFabric.volumeDiscountProfile) {
			volumeDiscountProfileItemList = selectedFabric.volumeDiscountProfile.volumeDiscountProfileItemList
				.sort((a, b) => b.minimumOrderQuantity - a.minimumOrderQuantity);
		}
		else if(product.madeToOrderFabric && product.madeToOrderFabric.volumeDiscountProfile) {
			volumeDiscountProfileItemList = product.madeToOrderFabric.volumeDiscountProfile.volumeDiscountProfileItemList
				.sort((a, b) => b.minimumOrderQuantity - a.minimumOrderQuantity);
		}
		for (const item of volumeDiscountProfileItemList) {
			if (item.minimumOrderQuantity <= selectedFabricQuantity) {
				fabricPrice = this.calculateDiscountPrice(fabricPrice, item.discount);
				break;
			}
		}

		if(product.productGroup === 'finished')
			calculatedPrice = this.calculateDiscountPrice(product.price + selectFinishPrice + selectCustomSizePrice, selectedVDProfile.discount) + fabricPrice;
		else
			calculatedPrice = this.calculateDiscountPrice(makingCharges + selectFinishPrice + selectCustomSizePrice, selectedVDProfile.discount) + fabricPrice;
		return calculatedPrice;
	}

	public calculateDiscountPrice(price: number, discount: number): number {
		return price - (price * (discount / 100));
	}

	public calculatedMTOConsumedFabric(madeToOrderProfile: MadeToOrderProfile): number {
		return madeToOrderProfile.consumedFabric > 0? madeToOrderProfile.consumedFabric :  1;
	}
}
